// ============================================================================
// ADMIN DATING - LISTE DES SIGNALEMENTS PRO
// Gestion complète des signalements avec statistiques et design moderne
// Version professionnelle avec filtres avancés et actions rapides
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { SimpleSelect } from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Eye,
  Ban,
  Loader2,
  RefreshCw,
  Clock,
  User,
  ShieldAlert,
  ShieldCheck,
  Search,
  Calendar,
  TrendingUp,
  TrendingDown,
  FileWarning,
  UserX,
  MessageSquareWarning,
  Skull,
  AlertOctagon,
  Flag,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  Gavel,
  History,
  Image,
  Mail,
  Phone,
  MapPin,
  Sparkles,
  Shield
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../components/ui/use-toast';
import { applyDomainFilterToQuery } from '../../../hooks/useDomainQuery';
import { DatingReport, DatingProfile, DATING_REPORT_REASONS } from '../../../types/dating';
import { format, formatDistanceToNow, subDays, startOfDay, endOfDay, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

// ============================================================================
// TYPES
// ============================================================================

interface ReportWithProfiles extends DatingReport {
  reporter?: DatingProfile;
  reported?: DatingProfile;
}

type FilterStatus = 'all' | 'pending' | 'reviewed' | 'resolved' | 'dismissed';
type FilterReason = 'all' | 'fake_profile' | 'harassment' | 'inappropriate_content' | 'spam' | 'underage' | 'scam' | 'other';
type FilterPeriod = 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'last_month';

interface ReportStats {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  dismissed: number;
  bannedProfiles: number;
  avgResolutionTime: number;
  todayCount: number;
  weekCount: number;
  monthCount: number;
  growthRate: number;
  topReason: string;
  resolutionRate: number;
}

// ============================================================================
// UTILITAIRES
// ============================================================================

const REASON_ICONS: Record<string, React.ReactNode> = {
  fake_profile: <UserX className="w-4 h-4" />,
  harassment: <MessageSquareWarning className="w-4 h-4" />,
  inappropriate_content: <FileWarning className="w-4 h-4" />,
  spam: <AlertOctagon className="w-4 h-4" />,
  underage: <ShieldAlert className="w-4 h-4" />,
  scam: <Skull className="w-4 h-4" />,
  other: <Flag className="w-4 h-4" />,
};

const REASON_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  fake_profile: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
  harassment: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
  inappropriate_content: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30' },
  spam: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30' },
  underage: { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30' },
  scam: { bg: 'bg-rose-500/20', text: 'text-rose-300', border: 'border-rose-500/30' },
  other: { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-500/30' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode }> = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30', icon: <Clock className="w-3 h-3" /> },
  reviewed: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30', icon: <Eye className="w-3 h-3" /> },
  resolved: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', icon: <CheckCircle className="w-3 h-3" /> },
  dismissed: { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-500/30', icon: <XCircle className="w-3 h-3" /> },
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  reviewed: 'En cours',
  resolved: 'Résolu',
  dismissed: 'Rejeté',
};

const MiniBarChart = ({ data, color = 'orange' }: { data: number[]; color?: string }) => {
  const max = Math.max(...data, 1);
  const colorClasses: Record<string, string> = { orange: 'bg-orange-500', red: 'bg-red-500', green: 'bg-green-500', blue: 'bg-blue-500' };
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((value, index) => (
        <div key={index} className={`w-2 ${colorClasses[color]} rounded-t opacity-70 hover:opacity-100 transition-all`}
          style={{ height: `${Math.max((value / max) * 100, 5)}%` }} title={`${value}`} />
      ))}
    </div>
  );
};

const ProgressBar = ({ value, max, color = 'orange' }: { value: number; max: number; color?: string }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const colorClasses: Record<string, string> = {
    orange: 'from-orange-500 to-amber-500',
    red: 'from-red-500 to-rose-500',
    green: 'from-green-500 to-emerald-500',
    blue: 'from-blue-500 to-cyan-500',
  };
  return (
    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
      <div className={`h-full bg-gradient-to-r ${colorClasses[color] || colorClasses.orange} transition-all duration-500`}
        style={{ width: `${percentage}%` }} />
    </div>
  );
};

const CircularProgress = ({ value, max, color = 'orange' }: { value: number; max: number; color?: string }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const radius = 24; const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  const colorClasses: Record<string, string> = { orange: 'stroke-orange-500', green: 'stroke-green-500', red: 'stroke-red-500', blue: 'stroke-blue-500' };
  return (
    <div className="relative" style={{ width: 60, height: 60 }}>
      <svg className="transform -rotate-90" width={60} height={60}>
        <circle cx={30} cy={30} r={radius} strokeWidth={6} fill="none" className="stroke-white/10" />
        <circle cx={30} cy={30} r={radius} strokeWidth={6} fill="none" strokeLinecap="round"
          className={`${colorClasses[color]} transition-all duration-500`}
          style={{ strokeDasharray: circumference, strokeDashoffset: offset }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-white">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
};

export default function DatingReportList() {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [reports, setReports] = useState<ReportWithProfiles[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterReason, setFilterReason] = useState<FilterReason>('all');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [selectedReport, setSelectedReport] = useState<ReportWithProfiles | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState<ReportStats>({
    total: 0, pending: 0, reviewed: 0, resolved: 0, dismissed: 0,
    bannedProfiles: 0, avgResolutionTime: 0, todayCount: 0, weekCount: 0,
    monthCount: 0, growthRate: 0, topReason: 'N/A', resolutionRate: 0
  });
  const [weeklyData, setWeeklyData] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('dating_reports')
        .select(`
          *,
          reporter:dating_profiles!dating_reports_reporter_id_fkey (id, display_name, avatar_url, email, phone, city, is_verified, is_vip),
          reported:dating_profiles!dating_reports_reported_id_fkey (id, display_name, avatar_url, email, phone, city, is_banned, is_verified, is_vip, total_likes_received, total_matches)
        `)
        .order('created_at', { ascending: false });

      query = applyDomainFilterToQuery(query);
      const { data, error } = await query;

      if (error) throw error;
      const allReports = (data || []) as ReportWithProfiles[];
      setReports(allReports);
      calculateStats(allReports);
    } catch (error) {
      console.error('Erreur chargement signalements:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les signalements', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateStats = (allReports: ReportWithProfiles[]) => {
    const today = new Date();
    const startOfToday = startOfDay(today);
    const weekAgo = subDays(today, 7);
    const thisMonthStart = startOfMonth(today);
    const lastMonthStart = startOfMonth(subMonths(today, 1));
    const lastMonthEnd = endOfMonth(subMonths(today, 1));

    const pending = allReports.filter(r => r.status === 'pending').length;
    const reviewed = allReports.filter(r => r.status === 'reviewed').length;
    const resolved = allReports.filter(r => r.status === 'resolved').length;
    const dismissed = allReports.filter(r => r.status === 'dismissed').length;

    const todayCount = allReports.filter(r => new Date(r.created_at) >= startOfToday).length;
    const weekCount = allReports.filter(r => new Date(r.created_at) >= weekAgo).length;
    const monthCount = allReports.filter(r => new Date(r.created_at) >= thisMonthStart).length;
    const lastMonthCount = allReports.filter(r => {
      const d = new Date(r.created_at);
      return d >= lastMonthStart && d <= lastMonthEnd;
    }).length;

    const growthRate = lastMonthCount > 0 ? ((monthCount - lastMonthCount) / lastMonthCount * 100) : 0;
    const resolutionRate = allReports.length > 0 ? ((resolved + dismissed) / allReports.length * 100) : 0;
    const bannedProfiles = allReports.filter(r => r.reported?.is_banned).length;

    const reasonCounts: Record<string, number> = {};
    allReports.forEach(r => { reasonCounts[r.reason] = (reasonCounts[r.reason] || 0) + 1; });
    const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    const resolvedReports = allReports.filter(r => r.reviewed_at && (r.status === 'resolved' || r.status === 'dismissed'));
    const avgResolutionTime = resolvedReports.length > 0
      ? resolvedReports.reduce((sum, r) => {
          const created = new Date(r.created_at).getTime();
          const resolvedAt = new Date(r.reviewed_at!).getTime();
          return sum + (resolvedAt - created) / (1000 * 60 * 60);
        }, 0) / resolvedReports.length
      : 0;

    const weeklyStats = [];
    for (let i = 6; i >= 0; i--) {
      const day = subDays(today, i);
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const count = allReports.filter(r => {
        const d = new Date(r.created_at);
        return d >= dayStart && d <= dayEnd;
      }).length;
      weeklyStats.push(count);
    }
    setWeeklyData(weeklyStats);

    setStats({
      total: allReports.length, pending, reviewed, resolved, dismissed,
      bannedProfiles, avgResolutionTime: Math.round(avgResolutionTime),
      todayCount, weekCount, monthCount,
      growthRate: Math.round(growthRate * 10) / 10,
      topReason, resolutionRate: Math.round(resolutionRate * 10) / 10
    });
  };

  useEffect(() => { fetchReports(); }, [tenant]);

  const filteredAndSortedReports = useMemo(() => {
    let result = [...reports];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(report =>
        report.reporter?.display_name?.toLowerCase().includes(q) ||
        report.reporter?.email?.toLowerCase().includes(q) ||
        report.reported?.display_name?.toLowerCase().includes(q) ||
        report.reported?.email?.toLowerCase().includes(q) ||
        report.description?.toLowerCase().includes(q)
      );
    }

    if (filterStatus !== 'all') result = result.filter(r => r.status === filterStatus);
    if (filterReason !== 'all') result = result.filter(r => r.reason === filterReason);

    if (filterPeriod !== 'all') {
      const today = new Date();
      let startDate: Date; let endDate: Date = endOfDay(today);
      switch (filterPeriod) {
        case 'today': startDate = startOfDay(today); break;
        case 'yesterday': startDate = startOfDay(subDays(today, 1)); endDate = endOfDay(subDays(today, 1)); break;
        case 'week': startDate = subDays(today, 7); break;
        case 'month': startDate = startOfMonth(today); break;
        case 'last_month': startDate = startOfMonth(subMonths(today, 1)); endDate = endOfMonth(subMonths(today, 1)); break;
        default: startDate = new Date(0);
      }
      result = result.filter(r => {
        const reportDate = new Date(r.created_at);
        return reportDate >= startDate && reportDate <= endDate;
      });
    }

    return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [reports, searchQuery, filterStatus, filterReason, filterPeriod]);

  const totalPages = Math.ceil(filteredAndSortedReports.length / itemsPerPage);
  const paginatedReports = filteredAndSortedReports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterStatus, filterReason, filterPeriod]);

  const handleRefresh = () => { setRefreshing(true); fetchReports(); };

  const getReasonLabel = (reason: string): string => {
    const found = DATING_REPORT_REASONS.find(r => r.value === reason);
    return found?.label || reason;
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.pending;
    return (
      <Badge className={`${config.bg} ${config.text} border ${config.border} gap-1`}>
        {config.icon}
        {STATUS_LABELS[status] || status}
      </Badge>
    );
  };

  const getReasonBadge = (reason: string) => {
    const config = REASON_COLORS[reason] || REASON_COLORS.other;
    const icon = REASON_ICONS[reason] || REASON_ICONS.other;
    return (
      <Badge className={`${config.bg} ${config.text} border ${config.border} gap-1`}>
        {icon}
        {getReasonLabel(reason)}
      </Badge>
    );
  };

  const handleReview = async (action: 'resolve' | 'dismiss' | 'ban' | 'warn') => {
    if (!selectedReport) return;

    setProcessing(true);
    try {
      const { error: reportError } = await supabase
        .from('dating_reports')
        .update({
          status: action === 'dismiss' ? 'dismissed' : 'resolved',
          reviewed_at: new Date().toISOString(),
          resolution: resolution || getDefaultResolution(action)
        })
        .eq('id', selectedReport.id);

      if (reportError) throw reportError;

      if (action === 'ban' && selectedReport.reported_id) {
        const { error: banError } = await supabase
          .from('dating_profiles')
          .update({
            is_banned: true,
            ban_reason: `Signalement: ${getReasonLabel(selectedReport.reason)}`,
            banned_at: new Date().toISOString(),
            is_active: false,
            is_visible: false
          })
          .eq('id', selectedReport.reported_id);

        if (banError) throw banError;
      }

      const messages: Record<string, { title: string; description: string }> = {
        ban: { title: '🚫 Profil banni', description: `Le profil ${selectedReport.reported?.display_name} a été banni.` },
        dismiss: { title: '❌ Signalement rejeté', description: 'Le signalement a été rejeté.' },
        resolve: { title: '✅ Signalement résolu', description: 'Le signalement a été traité avec succès.' },
        warn: { title: '⚠️ Avertissement envoyé', description: 'Un avertissement a été enregistré.' },
      };

      toast(messages[action]);
      setShowReviewModal(false);
      setSelectedReport(null);
      setResolution('');
      fetchReports();
    } catch (error) {
      console.error('Erreur traitement signalement:', error);
      toast({ title: 'Erreur', description: 'Impossible de traiter le signalement', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const getDefaultResolution = (action: string): string => {
    switch (action) {
      case 'ban': return 'Profil banni suite au signalement';
      case 'dismiss': return 'Signalement rejeté - non fondé';
      case 'resolve': return 'Signalement traité et résolu';
      case 'warn': return 'Avertissement envoyé à l\'utilisateur';
      default: return 'Traité';
    }
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Signalé par', 'Email signaleur', 'Profil signalé', 'Email signalé', 'Raison', 'Description', 'Statut', 'Date création', 'Date traitement', 'Résolution'];
    const rows = filteredAndSortedReports.map(r => [
      r.id, r.reporter?.display_name || 'N/A', r.reporter?.email || 'N/A',
      r.reported?.display_name || 'N/A', r.reported?.email || 'N/A',
      getReasonLabel(r.reason), r.description || '', STATUS_LABELS[r.status] || r.status,
      format(new Date(r.created_at), 'dd/MM/yyyy HH:mm'),
      r.reviewed_at ? format(new Date(r.reviewed_at), 'dd/MM/yyyy HH:mm') : '',
      r.resolution || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `signalements-dating-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: '📥 Export réussi', description: `${filteredAndSortedReports.length} signalements exportés` });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-orange-500/20 border-t-orange-500 animate-spin" />
            <ShieldAlert className="w-6 h-6 text-orange-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-400 text-lg">Chargement des signalements...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <Card className="relative overflow-hidden bg-gradient-to-r from-orange-600 via-amber-600 to-red-700 border-none shadow-2xl">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-20 w-40 h-40 bg-orange-300/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          <CardContent className="relative py-8 px-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <Link to="/admin/dating">
                  <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" /><span>Retour</span>
                  </Button>
                </Link>
                <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg">
                  <ShieldAlert className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-4xl font-bold text-white tracking-tight">Signalements Dating</h1>
                    <Badge className="bg-white/20 text-white border-white/30 text-sm px-3 py-1">{stats.total} signalements</Badge>
                    {stats.pending > 0 && (
                      <Badge className="bg-red-500/30 text-red-100 border-red-400/30 text-sm px-3 py-1 animate-pulse">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />{stats.pending} en attente
                      </Badge>
                    )}
                  </div>
                  <p className="text-orange-100 mt-2 text-base max-w-xl">
                    Modérez les signalements, protégez la communauté et maintenez un environnement sûr.
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-orange-100/90">
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                      <Calendar className="w-4 h-4" />{format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                      <Clock className="w-4 h-4" />Temps moyen: {stats.avgResolutionTime}h
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button variant="outline" onClick={handleExportCSV} className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm">
                  <Download className="w-4 h-4 mr-2" />Exporter
                </Button>
                <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm">
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Actualiser
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ALERTE */}
        {stats.pending > 0 && (
          <Card className="border-red-500/50 bg-gradient-to-r from-red-500/15 to-orange-500/10 backdrop-blur-sm">
            <CardContent className="py-4 px-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/30 animate-pulse">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-100 text-lg">{stats.pending} signalement{stats.pending > 1 ? 's' : ''} nécessite{stats.pending > 1 ? 'nt' : ''} votre attention</p>
                    <p className="text-sm text-red-200/80">Traitez rapidement pour maintenir la sécurité de la communauté.</p>
                  </div>
                </div>
                <Button onClick={() => setFilterStatus('pending')} className="bg-red-500 hover:bg-red-600 text-white shadow-lg">
                  <Gavel className="w-4 h-4 mr-2" />Traiter maintenant
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STATS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-orange-500/20 via-orange-500/10 to-amber-500/5 border-orange-500/30 hover:border-orange-500/50 transition-all hover:shadow-lg hover:shadow-orange-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-200/80">Total signalements</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {stats.growthRate >= 0 ? (
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs"><TrendingUp className="w-3 h-3 mr-1" />+{stats.growthRate}%</Badge>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs"><TrendingDown className="w-3 h-3 mr-1" />{stats.growthRate}%</Badge>
                    )}
                  </div>
                </div>
                <div className="p-2.5 bg-orange-500/20 rounded-xl"><Flag className="w-6 h-6 text-orange-300" /></div>
              </div>
              <div className="mt-4"><MiniBarChart data={weeklyData} color="orange" /><p className="text-xs text-orange-200/60 mt-1">7 derniers jours</p></div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500/20 via-yellow-500/10 to-amber-500/5 border-yellow-500/30 hover:border-yellow-500/50 transition-all hover:shadow-lg hover:shadow-yellow-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-200/80">En attente</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.pending}</p>
                  <p className="text-xs text-yellow-200/60 mt-3">À traiter en priorité</p>
                </div>
                <CircularProgress value={stats.pending} max={stats.total} color="orange" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 via-green-500/10 to-emerald-500/5 border-green-500/30 hover:border-green-500/50 transition-all hover:shadow-lg hover:shadow-green-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-green-200/80">Résolus</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.resolved}</p>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs mt-3"><CheckCircle className="w-3 h-3 mr-1" />{stats.resolutionRate}%</Badge>
                </div>
                <div className="p-2.5 bg-green-500/20 rounded-xl"><ShieldCheck className="w-6 h-6 text-green-300" /></div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-xs"><span className="text-green-200/70">Taux de résolution</span><span className="text-white font-medium">{stats.resolutionRate}%</span></div>
                <ProgressBar value={stats.resolutionRate} max={100} color="green" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500/20 via-red-500/10 to-rose-500/5 border-red-500/30 hover:border-red-500/50 transition-all hover:shadow-lg hover:shadow-red-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-red-200/80">Profils bannis</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.bannedProfiles}</p>
                  <p className="text-xs text-red-200/60 mt-3">Suite aux signalements</p>
                </div>
                <div className="p-2.5 bg-red-500/20 rounded-xl"><Ban className="w-6 h-6 text-red-300" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 via-purple-500/10 to-violet-500/5 border-purple-500/30 hover:border-purple-500/50 transition-all hover:shadow-lg hover:shadow-purple-500/10">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-200/80">Raison principale</p>
                  <p className="text-lg font-bold text-white mt-1">{getReasonLabel(stats.topReason)}</p>
                  <p className="text-xs text-purple-200/60 mt-3">Aujourd'hui: {stats.todayCount}</p>
                </div>
                <div className="p-2.5 bg-purple-500/20 rounded-xl">{REASON_ICONS[stats.topReason] || <Flag className="w-6 h-6 text-purple-300" />}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FILTRES */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-center">
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Rechercher par nom, email, description..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400" />
              </div>
              <SimpleSelect value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)} options={[
                { value: 'all', label: 'Tous les statuts' },
                { value: 'pending', label: '⏳ En attente' },
                { value: 'reviewed', label: '👁️ En cours' },
                { value: 'resolved', label: '✅ Résolus' },
                { value: 'dismissed', label: '❌ Rejetés' },
              ]} />
              <SimpleSelect value={filterReason} onValueChange={(v) => setFilterReason(v as FilterReason)} options={[
                { value: 'all', label: 'Toutes les raisons' },
                { value: 'fake_profile', label: '👤 Faux profil' },
                { value: 'harassment', label: '💬 Harcèlement' },
                { value: 'inappropriate_content', label: '⚠️ Contenu inapproprié' },
                { value: 'spam', label: '📢 Spam' },
                { value: 'underage', label: '🔞 Mineur' },
                { value: 'scam', label: '💀 Arnaque' },
                { value: 'other', label: '🏷️ Autre' },
              ]} />
              <SimpleSelect value={filterPeriod} onValueChange={(v) => setFilterPeriod(v as FilterPeriod)} options={[
                { value: 'all', label: 'Toutes les périodes' },
                { value: 'today', label: "📅 Aujourd'hui" },
                { value: 'yesterday', label: '📅 Hier' },
                { value: 'week', label: '📅 7 derniers jours' },
                { value: 'month', label: '📅 Ce mois' },
                { value: 'last_month', label: '📅 Mois dernier' },
              ]} />
            </div>
            {(filterStatus !== 'all' || filterReason !== 'all' || filterPeriod !== 'all' || searchQuery) && (
              <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                <span>{filteredAndSortedReports.length} résultat{filteredAndSortedReports.length > 1 ? 's' : ''}</span>
                <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setFilterStatus('all'); setFilterReason('all'); setFilterPeriod('all'); }} className="text-gray-400 hover:text-white">
                  <X className="w-3 h-3 mr-1" />Réinitialiser
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TABLE */}
        <Card className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
          <CardContent className="p-0">
            {filteredAndSortedReports.length === 0 ? (
              <div className="text-center py-16">
                <ShieldCheck className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucun signalement trouvé</p>
                <p className="text-gray-500 text-sm mt-1">Modifiez vos filtres ou la communauté est saine !</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10 hover:bg-transparent">
                      <TableHead className="text-gray-400">Signalé par</TableHead>
                      <TableHead className="text-gray-400">Profil signalé</TableHead>
                      <TableHead className="text-gray-400">Raison</TableHead>
                      <TableHead className="text-gray-400">Statut</TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                      <TableHead className="text-gray-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedReports.map((report) => (
                      <TableRow key={report.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border border-white/10">
                                {report.reporter?.avatar_url ? (
                                  <img src={report.reporter.avatar_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              {report.reporter?.is_verified && (
                                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                                  <CheckCircle className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-sm text-white flex items-center gap-1.5">
                                {report.reporter?.display_name || 'Inconnu'}
                                {report.reporter?.is_vip && <Sparkles className="w-3.5 h-3.5 text-yellow-400" />}
                              </div>
                              <div className="text-xs text-gray-400">{report.reporter?.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border border-white/10">
                                {report.reported?.avatar_url ? (
                                  <img src={report.reported.avatar_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              {report.reported?.is_banned && (
                                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                                  <Ban className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-sm text-white flex items-center gap-1.5">
                                {report.reported?.display_name || 'Inconnu'}
                                {report.reported?.is_banned && <Badge variant="destructive" className="text-xs py-0 px-1.5">Banni</Badge>}
                              </div>
                              <div className="text-xs text-gray-400">{report.reported?.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {getReasonBadge(report.reason)}
                            {report.description && (
                              <p className="text-xs text-gray-400 max-w-[200px] truncate" title={report.description}>{report.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(report.status)}</TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-300">
                            <div>{format(new Date(report.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}</div>
                            <div className="text-xs text-gray-500">{formatDistanceToNow(new Date(report.created_at), { addSuffix: true, locale: fr })}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant={report.status === 'pending' || report.status === 'reviewed' ? 'default' : 'ghost'}
                            onClick={() => { setSelectedReport(report); setShowReviewModal(true); }}
                            className={
                              report.status === 'pending' || report.status === 'reviewed'
                                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                : 'text-gray-200 hover:text-white'
                            }
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {report.status === 'pending' || report.status === 'reviewed' ? 'Traiter' : 'Voir'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
                    <p className="text-sm text-gray-400">Page {currentPage} sur {totalPages} ({filteredAndSortedReports.length} signalements)</p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-white/10 text-gray-300 hover:bg-white/5">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                        else pageNum = currentPage - 2 + i;
                        return (
                          <Button key={pageNum} variant={currentPage === pageNum ? 'default' : 'outline'} size="sm" onClick={() => setCurrentPage(pageNum)}
                            className={currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600' : 'border-white/10 text-gray-300 hover:bg-white/5'}>
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-white/10 text-gray-300 hover:bg-white/5">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* MODAL DE TRAITEMENT */}
        {showReviewModal && selectedReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowReviewModal(false)} />
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Gavel className="w-6 h-6 text-white" /></div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Traiter le signalement</h2>
                      <p className="text-orange-100 text-sm">Signalement de {selectedReport.reported?.display_name}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowReviewModal(false)} className="text-white/80 hover:text-white hover:bg-white/10">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Profils */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Signalé par</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                        {selectedReport.reporter?.avatar_url ? (
                          <img src={selectedReport.reporter.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{selectedReport.reporter?.display_name}</p>
                        <p className="text-xs text-gray-400">{selectedReport.reporter?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Profil signalé</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                        {selectedReport.reported?.avatar_url ? (
                          <img src={selectedReport.reported.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white flex items-center gap-2">
                          {selectedReport.reported?.display_name}
                          {selectedReport.reported?.is_banned && <Badge variant="destructive" className="text-xs">Banni</Badge>}
                        </p>
                        <p className="text-xs text-gray-400">{selectedReport.reported?.email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Raison</span>
                    {getReasonBadge(selectedReport.reason)}
                  </div>
                  {selectedReport.description && (
                    <div>
                      <span className="text-sm text-gray-400">Description</span>
                      <p className="text-sm mt-1 text-white bg-white/5 rounded-lg p-3">{selectedReport.description}</p>
                    </div>
                  )}
                  {selectedReport.evidence_urls && selectedReport.evidence_urls.length > 0 && (
                    <div>
                      <span className="text-sm text-gray-400 flex items-center gap-2"><Image className="w-4 h-4" />Preuves ({selectedReport.evidence_urls.length})</span>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {selectedReport.evidence_urls.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="group relative">
                            <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg border border-white/10 group-hover:border-orange-500 transition-colors" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <ExternalLink className="w-5 h-5 text-white" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Date du signalement</span>
                    <span className="text-white">{format(new Date(selectedReport.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}</span>
                  </div>
                </div>

                {/* Résolution */}
                {(selectedReport.status === 'pending' || selectedReport.status === 'reviewed') && (
                  <div>
                    <label className="text-sm font-medium text-white flex items-center gap-2"><History className="w-4 h-4" />Note de résolution (optionnel)</label>
                    <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Ajouter une note de résolution..." className="mt-2 bg-white/5 border-white/10 text-white placeholder-gray-500" rows={3} />
                  </div>
                )}

                {/* Résolution existante */}
                {selectedReport.resolution && (
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                    <span className="text-sm font-medium text-green-400 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Résolution</span>
                    <p className="text-sm text-green-300 mt-2">{selectedReport.resolution}</p>
                    {selectedReport.reviewed_at && (
                      <p className="text-xs text-green-400/60 mt-2">Traité le {format(new Date(selectedReport.reviewed_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur-sm border-t border-white/10 p-6 rounded-b-2xl">
                {(selectedReport.status === 'pending' || selectedReport.status === 'reviewed') ? (
                  <div className="flex flex-wrap gap-3 justify-end">
                    <Button variant="outline" onClick={() => handleReview('dismiss')} disabled={processing} className="border-gray-500/30 text-gray-300 hover:bg-gray-500/10">
                      <XCircle className="w-4 h-4 mr-2" />Rejeter
                    </Button>
                    <Button variant="outline" onClick={() => handleReview('warn')} disabled={processing} className="border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10">
                      <AlertTriangle className="w-4 h-4 mr-2" />Avertir
                    </Button>
                    <Button variant="outline" onClick={() => handleReview('resolve')} disabled={processing} className="border-green-500/30 text-green-300 hover:bg-green-500/10">
                      <CheckCircle className="w-4 h-4 mr-2" />Résoudre
                    </Button>
                    {!selectedReport.reported?.is_banned && (
                      <Button onClick={() => handleReview('ban')} disabled={processing} className="bg-red-500 hover:bg-red-600 text-white">
                        {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                        Bannir le profil
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setShowReviewModal(false)} className="border-white/10 text-gray-300 hover:bg-white/5">Fermer</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
